import { useCallback, useState } from "react";

import { observer } from "mobx-react-lite";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";

import iCircleOrange from "../../../assets/images/100-circle-orange.svg";
import iFavorites from "../../../assets/images/100-favorites.svg";
import iAdd from "../../../assets/images/100-plus-gray.svg";
import iTimer from "../../../assets/images/100-timer.svg";
import iError from "../../../assets/images/error.svg";
import { useMeetingDetailsStore } from "../../../contexts/meetingDetails/meetingDetailsContext";
import { useApi } from "../../../hooks/useApi";
import useScheduledDate from "../../../hooks/useScheduledDate";
import { EndLiveWorkspace } from "../../modals/end-live-workspace";

export const MeetingInfoPanel = observer(() => {
  const { meetingId } = useParams();
  const { endMeeting, leaveMeeting } = useApi();
  const { meeting, hasPermission, isJoined, setIsLeaving, initLeaving, updateInitLeaving } = useMeetingDetailsStore();
  const { formattedDate, endTime } = useScheduledDate({
    startsAt: meeting?.startsAt,
    endsAt: meeting?.endsAt,
  });
  const [openModal, setOpenModal] = useState(false);
  const [conferenceEndMeeting, setConferenceEndMeeting] = useState(false);
  // const [initLeaving, updateInitLeaving] = useState(false);

  const endMeetingHandler = async () => {
    await endMeeting(meeting.mid);
    setConferenceEndMeeting(true);
    setOpenModal(false);
  };

  const closeModalHandler = () => {
    setOpenModal(false);
    setConferenceEndMeeting(false);
  };

  const renderEndMeetingButton = useCallback(() => {
    if (hasPermission["end-meeting"]) {
      return (
        <div className="justify-content-center align-items-center fg-text-red B2-Medium b-rad-4 end-meeting">
          <span className="pointer" onClick={() => setOpenModal(true)}>
            End Meeting
          </span>
        </div>
      );
    }

    return null;
  }, [hasPermission["end-meeting"]]);

  const handleLeaveProcess = useCallback(() => {
    if (!initLeaving) {
      updateInitLeaving(true);
      leaveMeeting(meetingId)
        .then(() => {
          setIsLeaving(true);
        })
        .catch((e) => console.log(e));
    }
  }, []);

  const renderLiveWorkspaceState = (status) => {
    switch (status) {
      case "created":
        return "SCHEDULED";
      case "starting":
        return "STARTING";
      case "start-error":
        return "FAILED TO START";
      case "started":
        return "LIVE";
      case "ending":
        return "ENDING";
      case "ended":
        return "ENDED";
      default:
        return "Unknown status";
    }
  };

  const renderLiveWorkspaceStatus = (status) => {
    switch (status) {
      case "created":
        return `Live Workspace starts on ${formattedDate()}`;
      case "start-error":
        return `Live Workspace failed to start at ${formattedDate()}`;
      case "ending":
      case "starting":
        return false;
      case "started":
        return false;
      case "ended":
        return `Live Workspace was scheduled to end at ${endTime()}`;
      default:
        return "Unknown status";
    }
  };

  const handleExtendTime = () => {
    console.log("handleExtendTime click");
  };

  return (
    <div className="flex-col w-650 page-header-data">
      <div className="align-items-center text-nowrap m-b-4">
        <div className="align-items-center SH1-SemiBold h-32">{meeting.title}</div>
        <div className="chip C1-Medium m-lr-12"> Live Workspace</div>
        <div className="flex add-to-favorite pointer">
          <img alt="Add to favorites" src={iFavorites} className="i-24" />
          <div className="tooltip-type2">Add to favorites</div>
        </div>
      </div>
      <div className="flex h-36 gap-16">
        <div className="align-items-center">
          <img alt="" src={iCircleOrange} className="i-12" />
          <span className="B1-SemiBold m-l-6"> {renderLiveWorkspaceState(meeting.status)}</span>
        </div>
        <div className="align-items-center">
          <img alt="" src={iTimer} className="i-20" />
          <span className="B2-Medium m-l-6"> {meeting.duration} </span>
        </div>
        {!!renderLiveWorkspaceStatus(meeting.status) && (
          <div className="align-items-center">
            <img alt="" src={iError} className="i-20" />
            <span className="B2-Medium m-l-5 width-max-content">{renderLiveWorkspaceStatus(meeting.status)}</span>
          </div>
        )}
        {meeting.status === "started" && (
          <>
            <div className="justify-content-center align-items-center B2-Medium pointer extend-meeting b-rad-4" onClick={handleExtendTime}>
              <img alt="" src={iAdd} className="i i-12" />
              <span className="B2-Medium m-l-5 width-max-content">Extend Time</span>
              <div className="tooltip-type2">Extend for 1 hour</div>
            </div>
            {renderEndMeetingButton()}
            {isJoined && (
              <div
                className={`justify-content-center align-items-center B2-Medium b-rad-4 leave-meeting ${
                  !initLeaving ? "fg-text-red" : "fg-text-gray-light disabled"
                }`}>
                <span className={!initLeaving && "pointer"} onClick={handleLeaveProcess}>
                  Leave
                </span>
              </div>
            )}
          </>
        )}
        {openModal &&
          createPortal(
            <EndLiveWorkspace
              conferenceEndMeeting={conferenceEndMeeting}
              endMeetingHandler={endMeetingHandler}
              closeModalHandler={closeModalHandler}
            />,
            document.body,
          )}
      </div>
    </div>
  );
});
